import type { FormFieldOptions } from "@tellescope/types-models";

export interface DateOffsetBounds {
  withinBounds: boolean;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Resolves a field's `minDateOffsetMs`/`maxDateOffsetMs` into concrete bounds relative to now and
 * reports whether `date` falls inside them. The resolved bounds are returned so callers can format
 * them into error messages.
 *
 * The two normalization flags exist because the call sites genuinely disagree and the differences
 * are observable:
 * @param normalizeMinToMidnight - when true, `minDate` drops its time-of-day. `validateFieldByType`'s
 *   `date` case compares against the raw offset instead, so it passes false.
 * @param maxBoundary - `"midnight"` zeroes `maxDate`'s time; `"endOfDay"` pushes it to 23:59:59.999.
 */
export const isDateWithinOffsetBounds = (
  date: Date,
  options: FormFieldOptions | undefined,
  { normalizeMinToMidnight, maxBoundary }: { normalizeMinToMidnight: boolean; maxBoundary: "midnight" | "endOfDay" }
): DateOffsetBounds => {
  const result: DateOffsetBounds = { withinBounds: true };

  if (options?.minDateOffsetMs !== undefined) {
    const minDate = new Date(Date.now() + options.minDateOffsetMs);
    if (normalizeMinToMidnight) minDate.setHours(0, 0, 0, 0);
    result.minDate = minDate;
    if (date < minDate) result.withinBounds = false;
  }

  if (options?.maxDateOffsetMs !== undefined) {
    const maxDate = new Date(Date.now() + options.maxDateOffsetMs);
    if (maxBoundary === "endOfDay") maxDate.setHours(23, 59, 59, 999);
    else maxDate.setHours(0, 0, 0, 0);
    result.maxDate = maxDate;
    if (date > maxDate) result.withinBounds = false;
  }

  return result;
};
